import React, { useCallback, useState } from "react";
import toast from "react-hot-toast";
import HosesSection from "../sections/HosesSection";
import { PlusCircleIcon, CubeTransparentIcon } from "@heroicons/react/24/solid";
import { useWorkorderHose } from "../../../../../hooks/hoses/useWorkorderHoses";
import HoseDraftRow from "../../../../../components/workorders/hoses/HoseDraftRow";
import { t } from "i18next";
import { Button } from "@material-tailwind/react";

interface HosesTabProps {
  workorder: EasyTech.Workorder | null;
  canEdit?: boolean;
}

const HosesTab: React.FC<HosesTabProps> = ({
  workorder,
  canEdit = true
}) => {
  // État pour gérer l'affichage du formulaire d'ajout
  const [showHoseDraft, setShowHoseDraft] = useState(false);
  const { removeHose, addHose } = useWorkorderHose(workorder?.id || "");

  // Handler pour afficher le formulaire d'ajout
  const handleAddHose = () => {
    setShowHoseDraft(true);
  };

  // Handler pour annuler l'ajout
  const handleCancelAddHose = () => {
    setShowHoseDraft(false);
  };
  
  // Handler pour supprimer un hose
  const handleRemoveHose = useCallback(
    async (hoseId: string) => {
      try {
        await removeHose(hoseId);
      } catch (err) {
        console.error(t("workorder:errors:removeHose"), err);
      }
    },
    [removeHose]
  );

  // Handler pour sauvegarder un nouveau hose
  const handleSaveHose = async (hoseData: { name: string; description?: string; qrCode: string }) => {
    try {
      if (!workorder || !workorder.id) {
        toast.error(t("workorder:errors:invalidWorkorder"));
        return;
      }
      
      const newHoseData = {
        name: hoseData.name,
        description: hoseData.description,
        qrCode: hoseData.qrCode,
        workOrder: { id: workorder.id }
      };

      await addHose(newHoseData);
      toast.success(t("workorder:success:hoseAdded"));
      setShowHoseDraft(false);
    } catch (err) {
      console.error(t("workorder:errors:addHose"), err);
      toast.error(t("workorder:errors:addHose"));
    }
  };

  if (!workorder) return null;

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-medium text-gray-900">
          {t("workorder:editIntervention")} <span className="text-gray-500 text-sm">{workorder.ref}</span>
        </h2>
        <Button
          onClick={handleAddHose}
          variant="filled"
          size="md"
          className="flex items-center gap-2 bg-blue-50 shadow-md text-sm text-blue-500 px-3 rounded-lg font-thin normal-case"
          disabled={!canEdit}
        >
          <PlusCircleIcon className="h-5 w-5 text-blue-500" />
          {t("workorder:addHose")}
        </Button>
      </div>

      {/* Afficher le formulaire d'ajout si nécessaire */}
      {showHoseDraft && (
        <HoseDraftRow
          onSave={handleSaveHose}
          onCancel={handleCancelAddHose}
        />
      )}

      {workorder.hoses && workorder.hoses.length > 0 ? (
        <HosesSection
          workorderId={workorder.id}
          machineId=""
          targetType="workorder"
          onRemoveHose={handleRemoveHose}
          canEdit={canEdit}
        />
      ) : (
        !showHoseDraft && (
          <div className="text-center p-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <CubeTransparentIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600 font-medium">{t("workorder:hoses:noHose")}</p>
          </div>
        )
      )}
    </div>
  );
};

export default HosesTab;
